import React from 'react';
import { Link } from 'react-router-dom';
import { ETSY_SHOP_URL } from '../lib/constants';

export type PillarColor = 'green' | 'blue' | 'purple' | 'orange';

interface PillarPageLayoutProps {
  icon: string;
  title: string;
  subtitle: string;
  color: PillarColor;
  diagram: React.ReactNode;
  explanation: React.ReactNode;
  benefits: string[];
  scenarios: { emoji: string; label: string }[];
  productNote?: React.ReactNode;
}

const colorClasses: Record<PillarColor, { 
  bg: string; 
  iconBg: string; 
  check: string;
  badge: string;
}> = {
  green: { 
    bg: 'bg-green-50', 
    iconBg: 'bg-green-50', 
    check: 'text-green-600',
    badge: 'bg-green-100 text-green-700'
  },
  blue: { 
    bg: 'bg-blue-50', 
    iconBg: 'bg-blue-50', 
    check: 'text-blue-600',
    badge: 'bg-blue-100 text-blue-700'
  },
  purple: { 
    bg: 'bg-purple-50', 
    iconBg: 'bg-purple-50', 
    check: 'text-purple-600',
    badge: 'bg-purple-100 text-purple-700'
  },
  orange: { 
    bg: 'bg-orange-50', 
    iconBg: 'bg-orange-50', 
    check: 'text-orange-600',
    badge: 'bg-orange-100 text-orange-700'
  },
};

const PillarPageLayout: React.FC<PillarPageLayoutProps> = ({
  icon,
  title,
  subtitle,
  color,
  diagram,
  explanation,
  benefits,
  scenarios,
  productNote,
}) => {
  const colors = colorClasses[color];

  return (
    <div className="bg-slate-100 min-h-screen">
      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Back link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 text-sm font-medium"
        >
          ← Back to home
        </Link>

        {/* Main content card */}
        <article className="bg-white rounded-3xl p-8 md:p-14 shadow-sm">
          {/* Header */}
          <header className="flex items-center gap-4 mb-10">
            <div className={`text-5xl w-20 h-20 flex items-center justify-center rounded-2xl ${colors.iconBg}`}>
              {icon}
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900">{title}</h1>
              <p className="text-lg text-slate-500">{subtitle}</p>
            </div>
          </header>

          {/* Two column: diagram + explanation */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="bg-slate-50 rounded-2xl p-8">
              {diagram}
            </div>

            <div>
              {explanation}

              <ul className="mt-6 space-y-2">
                {benefits.map((benefit, i) => (
                  <li key={i} className="pl-8 relative text-slate-600 py-1">
                    <span className={`absolute left-0 font-bold ${colors.check}`}>✓</span>
                    <span dangerouslySetInnerHTML={{ __html: benefit }} />
                  </li>
                ))}
              </ul>

              {productNote && (
                <div className="mt-6 p-4 bg-slate-100 rounded-xl text-sm text-slate-600 flex gap-3 items-start">
                  {productNote}
                </div>
              )}

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {scenarios.map((scenario, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                    <span className="text-xl">{scenario.emoji}</span>
                    <span>{scenario.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Bottom CTA */}
        <div className={`mt-8 ${colors.bg} rounded-2xl p-8 text-center`}>
          <h3 className="text-2xl font-bold text-slate-900 mb-3">
            Ready to experience Megadish?
          </h3>
          <p className="text-slate-600 mb-6">
            Available on Etsy. Handmade in the UK.
          </p>
          <a
            href={ETSY_SHOP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-8 py-3.5 rounded-lg font-semibold transition-colors"
          >
            Shop on Etsy
          </a>
        </div>
      </div>
    </div>
  );
};

export default PillarPageLayout;
